import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { fmtEur } from "@/lib/format";
import {
  ArrowRight, Building2, BarChart3, Zap, CheckCircle2, TrendingUp,
  Calculator, Send, ShieldCheck, Globe,
} from "lucide-react";
import { toast } from "sonner";

const OPENPAYMENTS_FEE = 0.18; // EUR per SEPA Instant (demo)
const COMPETITOR_AVG_FEE_PCT = 0.65 / 100; // 0.65%

function StatCard({ k, v, sub, accent = "#2563EB" }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">{k}</div>
      <div className="font-display text-3xl font-bold mt-1" style={{ color: accent }}>{v}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Business() {
  // Calculator state
  const [volumePerMonth, setVolumePerMonth] = useState(50);   // # transfers / month
  const [avgAmount, setAvgAmount] = useState(2500);            // avg EUR per transfer

  const calc = useMemo(() => {
    const monthlyVolume = volumePerMonth * avgAmount;
    const competitorMonthly = monthlyVolume * COMPETITOR_AVG_FEE_PCT;
    const ourMonthly = volumePerMonth * OPENPAYMENTS_FEE;
    const savings = Math.max(competitorMonthly - ourMonthly, 0);
    return {
      monthlyVolume,
      competitorMonthly,
      ourMonthly,
      savings,
      yearlySavings: savings * 12,
    };
  }, [volumePerMonth, avgAmount]);

  // Lead form
  const [form, setForm] = useState({
    company: "", full_name: "", email: "", phone: "",
    monthly_volume: "100k - 500k EUR", use_case: "Tesorería B2B", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitLead = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api.post("/leads", form);
      setSent(true);
      toast.success("Solicitud enviada. Te contactaremos en menos de 24h.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-cosmos text-white" data-testid="business-page">
      <MarketingNav />

      {/* HERO B2B */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 hero-grid pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 border border-slate-200 px-3 py-1.5 rounded-full text-xs font-mono-fin text-slate-700"
              >
                <Building2 size={12} className="text-blue-600" /> Para empresas y fintechs
              </motion.div>
              <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter text-slate-900 mt-6 leading-[0.95]">
                Pagos SEPA<br/>
                <span className="text-blue-600">hasta 80% más baratos.</span>
              </h1>
              <p className="text-slate-600 text-lg max-w-xl mt-6 leading-relaxed">
                Cuentas IBAN dedicadas, API REST con webhooks, conciliación automática y comisiones planas.
                Diseñado para PYMEs, marketplaces y fintechs que mueven dinero por toda Europa.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href="#solicitar-api" className="btn-primary" data-testid="b2b-cta-request">
                  Solicitar acceso a la API <ArrowRight size={18} />
                </a>
                <a href="#calculadora" className="btn-secondary" data-testid="b2b-cta-calc">
                  <Calculator size={16} /> Calcular mi ahorro
                </a>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-5">
                <StatCard k="Tarifa SEPA Inst" v="€ 0,18" sub="por operación" />
                <StatCard k="Setup" v="0 €" sub="sin permanencia" accent="#16A34A" />
                <StatCard k="Liquidación" v="< 10s" sub="24/7/365" accent="#9333EA" />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-slate-900 text-white p-8 relative overflow-hidden">
                <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full border border-blue-700/30"></div>
                <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-300">Casos de uso</div>
                <div className="mt-4 space-y-3">
                  {[
                    "Marketplaces multi-vendor con pagos masivos",
                    "Plataformas SaaS con cobros recurrentes",
                    "Tesorería corporativa con liquidez 24/7",
                    "Wallets cripto con on/off-ramp regulado (MiCA)",
                    "Adquirencia de comercios y reconciliación",
                  ].map((x) => (
                    <div key={x} className="flex items-start gap-3">
                      <CheckCircle2 size={18} className="text-blue-400 shrink-0 mt-0.5" />
                      <span className="text-slate-200 text-sm">{x}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 border-t border-slate-700 pt-4 text-xs text-slate-400">
                  Documentación API · webhooks · IBANs virtuales por cliente final
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculadora" className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600 mb-3">Calculadora</div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter text-slate-900 leading-tight">
                ¿Cuánto ahorras al mes?
              </h2>
              <p className="text-slate-600 mt-4 text-lg">
                Compara tu coste actual con OpenPayments. La media de la banca tradicional cobra <b>0,65%</b> por
                transferencia SEPA. Nosotros: <b>€ 0,18</b> fijos.
              </p>

              <div className="mt-8 space-y-6 bg-white border border-slate-200 rounded-3xl p-6">
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-semibold text-slate-700">Transferencias / mes</label>
                    <span className="font-mono-fin text-blue-600 font-bold text-lg" data-testid="calc-volume">{volumePerMonth}</span>
                  </div>
                  <input type="range" min="1" max="2000" value={volumePerMonth} step="1"
                    onChange={(e) => setVolumePerMonth(Number(e.target.value))}
                    data-testid="calc-volume-input"
                    className="w-full accent-blue-600" />
                  <div className="flex justify-between text-[11px] font-mono-fin text-slate-400 mt-1">
                    <span>1</span><span>500</span><span>1.000</span><span>2.000</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-semibold text-slate-700">Importe medio (EUR)</label>
                    <span className="font-mono-fin text-blue-600 font-bold text-lg" data-testid="calc-avg">{fmtEur(avgAmount)}</span>
                  </div>
                  <input type="range" min="50" max="50000" value={avgAmount} step="50"
                    onChange={(e) => setAvgAmount(Number(e.target.value))}
                    data-testid="calc-avg-input"
                    className="w-full accent-blue-600" />
                  <div className="flex justify-between text-[11px] font-mono-fin text-slate-400 mt-1">
                    <span>€ 50</span><span>€ 10k</span><span>€ 25k</span><span>€ 50k</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-3xl p-8 text-white relative overflow-hidden shadow-[0_30px_60px_-20px_rgba(37,99,235,0.5)]"
                   style={{ background: "linear-gradient(135deg, #0A192F 0%, #1E3A8A 60%, #2563EB 100%)" }}>
                <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full border border-white/10"></div>
                <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-200">Tu ahorro estimado · al año</div>
                <div className="font-display font-black text-6xl md:text-7xl tracking-tighter mt-2" data-testid="calc-savings">
                  {fmtEur(calc.yearlySavings)}
                </div>
                <div className="text-blue-200 mt-2">Con OpenPayments vs. banca tradicional (0,65%)</div>

                <div className="mt-8 grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded-2xl p-4 backdrop-blur">
                    <div className="text-[11px] font-mono-fin uppercase tracking-widest text-blue-200">Volumen mensual</div>
                    <div className="font-display text-2xl font-bold mt-1">{fmtEur(calc.monthlyVolume)}</div>
                  </div>
                  <div className="bg-white/10 rounded-2xl p-4 backdrop-blur">
                    <div className="text-[11px] font-mono-fin uppercase tracking-widest text-blue-200">Ahorro mensual</div>
                    <div className="font-display text-2xl font-bold mt-1 text-green-300">{fmtEur(calc.savings)}</div>
                  </div>
                  <div className="bg-white/10 rounded-2xl p-4 backdrop-blur">
                    <div className="text-[11px] font-mono-fin uppercase tracking-widest text-blue-200">Tu coste actual</div>
                    <div className="font-display text-xl font-bold mt-1 text-red-300">{fmtEur(calc.competitorMonthly)} / mes</div>
                  </div>
                  <div className="bg-white/10 rounded-2xl p-4 backdrop-blur">
                    <div className="text-[11px] font-mono-fin uppercase tracking-widest text-blue-200">Con OpenPayments</div>
                    <div className="font-display text-xl font-bold mt-1 text-green-300">{fmtEur(calc.ourMonthly)} / mes</div>
                  </div>
                </div>

                <a href="#solicitar-api"
                   className="mt-8 inline-flex items-center gap-2 bg-white text-blue-700 px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-colors"
                   data-testid="calc-to-form">
                  <Send size={16} /> Quiero esta tarifa
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES B2B */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600 mb-3">Por qué OpenPayments</div>
          <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter text-slate-900 max-w-3xl">
            Una API. Toda Europa. Sin sorpresas.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { I: Globe,        t: "36 países", d: "Cobertura EEA + UK. Cuentas IBAN locales y virtuales por usuario." },
              { I: Zap,          t: "API REST",  d: "Endpoints documentados, webhooks tiempo real, SDKs en JS/Python." },
              { I: TrendingUp,   t: "Conciliación", d: "Match automático de movimientos con tus facturas/contratos." },
              { I: ShieldCheck,  t: "Reguladores", d: "FCA · MiCA · PSD2 · GDPR · auditorías SOC2 (en curso)." },
            ].map(({ I, t, d }) => (
              <div key={t} className="rounded-2xl bg-white border border-slate-200 p-6">
                <I size={22} className="text-blue-600" />
                <div className="font-display font-bold text-xl text-slate-900 mt-3">{t}</div>
                <div className="text-slate-600 text-sm mt-2 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="solicitar-api" className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600 mb-3">Solicitar acceso</div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter text-slate-900 leading-tight">
                Hablemos de tu caso.
              </h2>
              <p className="text-slate-600 mt-4 text-lg">
                Cuéntanos qué quieres construir. Un especialista te contactará en <b>menos de 24 horas</b> con una propuesta
                a medida, sandbox de pruebas y onboarding completo.
              </p>
              <div className="mt-8 space-y-4 text-sm text-slate-700">
                {[
                  ["✓", "Sandbox gratuito · sin tarjeta"],
                  ["✓", "IBANs ilimitados en el sandbox"],
                  ["✓", "Onboarding técnico con un ingeniero"],
                  ["✓", "Tarifa fija negociada según volumen"],
                ].map(([m, t]) => (
                  <div key={t} className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold text-xs">{m}</span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              {sent ? (
                <div className="rounded-3xl bg-green-50 border border-green-200 p-10 text-center" data-testid="lead-success">
                  <CheckCircle2 size={48} className="text-green-600 mx-auto" />
                  <h3 className="font-display font-bold text-3xl text-slate-900 mt-4">¡Solicitud recibida!</h3>
                  <p className="text-slate-600 mt-2 max-w-md mx-auto">
                    Hemos registrado tu interés. Un especialista te contactará pronto en <b>{form.email}</b>.
                  </p>
                  <Link to="/" className="btn-primary mt-6 inline-flex" data-testid="lead-back-home">Volver al inicio</Link>
                </div>
              ) : (
                <form onSubmit={submitLead} className="bg-white border border-slate-200 rounded-3xl p-8 space-y-5" data-testid="lead-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Empresa</label>
                      <input required value={form.company} onChange={(e) => set("company", e.target.value)}
                        data-testid="lead-company"
                        className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none" />
                    </div>
                    <div>
                      <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Nombre y apellido</label>
                      <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                        data-testid="lead-fullname"
                        className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Email corporativo</label>
                      <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                        data-testid="lead-email"
                        className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none" />
                    </div>
                    <div>
                      <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Teléfono</label>
                      <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                        data-testid="lead-phone"
                        className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Volumen mensual estimado</label>
                      <select value={form.monthly_volume} onChange={(e) => set("monthly_volume", e.target.value)}
                        data-testid="lead-volume"
                        className="w-full mt-1.5 px-3 py-3 rounded-xl border border-slate-200 bg-white outline-none">
                        {["< 50k EUR","50k - 100k EUR","100k - 500k EUR","500k - 2M EUR","2M - 10M EUR","> 10M EUR"].map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Caso de uso</label>
                      <select value={form.use_case} onChange={(e) => set("use_case", e.target.value)}
                        data-testid="lead-usecase"
                        className="w-full mt-1.5 px-3 py-3 rounded-xl border border-slate-200 bg-white outline-none">
                        {["Tesorería B2B","Marketplace multi-vendor","Wallets cripto / on-off-ramp","Adquirencia y comercios","SaaS / cobros recurrentes","Otro"].map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Cuéntanos más (opcional)</label>
                    <textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)}
                      data-testid="lead-message"
                      placeholder="¿Qué quieres construir? Stack, número de usuarios, países objetivo..."
                      className="w-full mt-1.5 px-4 py-3 rounded-2xl border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none" />
                  </div>
                  {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="lead-error">{error}</div>}
                  <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="lead-submit">
                    {submitting ? "Enviando..." : <>Solicitar acceso a la API <ArrowRight size={18} /></>}
                  </button>
                  <p className="text-[11px] text-slate-500 text-center">
                    Al enviar aceptas que OpenPayments te contacte. Tratamos tus datos según GDPR.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
